import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { EMonths } from '../../../utils/enums/months.enum';
import {
  IEvolutionDataReport,
  IEvolutionQuestionReportOLD,
  IEvolutionReport,
  IEvolutionReportOLD,
} from '../../../utils/interfaces/evolution-report';
import { IReportFilter } from '../../../utils/interfaces/report-filter';
import { IResponse } from '../../../utils/interfaces/response';

@Injectable({
  providedIn: 'root',
})
export class EvolutionReportService {
  private readonly URI_API = environment.apiConfig.uri;

  constructor(private readonly httpClient: HttpClient) { }

  getEvolutionReport(
    filter: IReportFilter
  ): Observable<IResponse<IEvolutionReport[]>> {
    return this.httpClient
      .post<IResponse<IEvolutionReportOLD>>(
        `${this.URI_API}EvolutionReport/evolution`,
        filter
      )
      .pipe(
        map(
          (res): IResponse<IEvolutionReport[]> =>
            this.transformDataEvolutionReport(filter, res)
        )
      );
  }

  getEvolutionByDaysReport(
    filter: IReportFilter
  ): Observable<IResponse<IEvolutionReport>> {
    return this.httpClient
      .post<IResponse<IEvolutionReportOLD>>(
        `${this.URI_API}EvolutionDaysReport/evolutiondays`,
        filter
      )
      .pipe(
        map(
          (res): IResponse<IEvolutionReport> =>
            this.transformDataEvolutionDays(res)
        )
      );
  }

  /**
   * @param filter filtro do relatório
   * @param res resposta get do relatório de evolução
   * @returns Resposta do relatório de evolução com o novo formato de dados
   * Transforma os dados do relatório de evolução para o novo formato de dados.
   */
  private transformDataEvolutionReport(
    filter: IReportFilter,
    res: IResponse<IEvolutionReportOLD>
  ): IResponse<IEvolutionReport[]> {
    let newData: IEvolutionReport[] = [];

    const allLabels = this.getMonthsInRange(
      new Date(filter.startDate),
      new Date(filter.finishDate)
    );

    newData.push(
      this.transformFirstQuestion(res.data.firstQuestion[0], allLabels)
    );

    res.data.othersQuestions.forEach((OQ) => {
      newData.push(this.transformOthersQuestions(OQ, allLabels));
    });

    const newRes: IResponse<IEvolutionReport[]> = {
      ...res,
      data: newData,
    };
    return newRes;
  }

  private transformDataEvolutionDays(res: IResponse<any>): IResponse<any> {
    const firstQuestion = res.data?.firstQuestion?.[0];
    const details: any[] = [...(firstQuestion?.detailsOthersQuestion ?? [])];

    // A chave da data é ano + mês + dia: sem o ano, dias iguais de anos
    // diferentes colidiam no mesmo ponto do gráfico.
    details.sort(
      (a: any, b: any) =>
        (a.year ?? 0) - (b.year ?? 0) || a.month - b.month || a.day - b.day
    );

    const spansMultipleYears =
      new Set(details.map((d: any) => d.year)).size > 1;

    const data: IEvolutionReport = {
      title: firstQuestion?.title ?? '',
      responses: firstQuestion?.countResponses ?? 0,
      labels: details.map((d: any) => this.getDayLabel(d, spansMultipleYears)),
      data: [
        {
          values: details.map((r: any) => r.percentage),
          label: 'Satisfação',
        },
      ],
    };
    return { ...res, data };
  }

  /**
   * @param detail Ponto diário do relatório (year/month/day)
   * @param includeYear Inclui o ano quando o período cruza mais de um ano
   * @returns Label da data no formato 'DD/Mês' ou 'DD/Mês/AAAA'
   */
  private getDayLabel(detail: any, includeYear: boolean): string {
    const day = detail.day < 10 ? `0${detail.day}` : `${detail.day}`;
    const label = `${day}/${EMonths[detail.month]}`;

    return includeYear && detail.year ? `${label}/${detail.year}` : label;
  }

  /**
   * @param firstQuestion primeira pergunta do relatório de evolução 'Principal'
   * @param labels Array de meses no formato 'Mês/Ano'
   * @returns Dados da primeira pergunta do relatório de evolução
   * Transforma os dados do relatório de evolução para o novo formato de dados.
   */
  private transformFirstQuestion(
    firstQuestion: IEvolutionQuestionReportOLD,
    labels: string[]
  ): IEvolutionReport {
    let dataset: IEvolutionReport = {
      title: firstQuestion?.title ?? '',
      responses: firstQuestion?.countResponses ?? 0,
      labels: [...labels],
      data: [
        {
          values: [],
          label: 'Satisfação',
        },
      ],
    };

    dataset.labels.forEach((l) => {
      const value =
        firstQuestion?.detailsOthersQuestion.find(
          (F: any) => `${EMonths[F.month]}/${F.year}` === l
        ) ?? null;

      if (value) {
        dataset.data[0].values.push(value.percentage);
      } else {
        dataset.data[0].values.push(0);
      }
    });

    return dataset;
  }

  /**
   * @param OQ Pergunta do relatório de evolução 'Outras'
   * @param allLabels Array de meses no formato 'Mês/Ano'
   * @returns Dados da pergunta do relatório de evolução 'Outras'
   * Transforma os dados do relatório de evolução para o novo formato de dados.
   */
  private transformOthersQuestions(
    OQ: IEvolutionQuestionReportOLD,
    allLabels: string[]
  ): IEvolutionReport {
    let v: IEvolutionReport = {
      title: OQ.title,
      responses: OQ.countResponses,
      labels: [],
      data: [],
    };

    v.labels = allLabels;

    const labels = [
      ...new Set(OQ.detailsOthersQuestion.map((i: any) => i.answer)),
    ];

    v.data = [
      ...labels.map((l) => {
        return { values: [], label: l };
      }),
    ] as IEvolutionDataReport[];

    v.data.forEach((d) => {
      const values = OQ.detailsOthersQuestion.filter(
        (F) => F.answer === d.label
      );

      v.labels.forEach((l) => {
        const value = values.find(
          (F: any) => `${EMonths[F.month]}/${F.year}` === l
        );

        if (value) {
          d.values.push(value.percentage);
        } else {
          d.values.push(0);
        }
      });
    });

    return v;
  }

  /**
   * @param start Data de início
   * @param finish Data final
   * @returns Array de meses no formato 'Mês/Ano' no intervalo de datas
   */
  private getMonthsInRange(start: Date, finish: Date) {
    const meses = [];
    let dataAtual = new Date(start);

    // Adiciona o primeiro mês (start)
    while (dataAtual <= finish) {
      const mesAno = `${EMonths[dataAtual.getMonth() + 1]
        }/${dataAtual.getFullYear()}`; // Formato MM/YYYY
      meses.push(mesAno);

      // Avança para o próximo mês
      dataAtual.setMonth(dataAtual.getMonth() + 1);
    }

    // A última iteração pode ter avançado para o próximo mês, então, verificamos a dataFim diretamente
    const mesAnoFinal = `${EMonths[finish.getMonth() + 1]
      }/${finish.getFullYear()}`;
    if (!meses.includes(mesAnoFinal)) {
      meses.push(mesAnoFinal);
    }

    return meses;
  }
}
